<template>
  <div class="">
    <div class="flex flex-wrap items-end gap-3 pb-4 mb-4 border-b border-gray-200">
      <!-- Select dropdown -->
      <div class="flex flex-col flex-1 min-w-[200px]">
        <label for="mobileSelect" class="mb-1 text-xs font-medium text-gray-500 uppercase tracking-wide">
          Select Mobile
        </label>
        <select
          id="mobileSelect"
          v-model="selectedMobile"
          class="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option
            v-for="(item, index) in userMobiles"
            :value="item.value"
            :key="index"
          >{{ item.label }} : {{ item.value }}</option>
          <option value="">Enter Manually</option>
        </select>
      </div>

      <!-- Manual input -->
      <div class="flex flex-col flex-1 min-w-[200px]">
        <label for="manualMobile" class="mb-1 text-xs font-medium text-gray-500 uppercase tracking-wide">
          Or Enter Number
        </label>
        <input
          id="manualMobile"
          v-model="selectedMobile"
          type="text"
          placeholder="e.g. 9876543210"
          class="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <!-- Actions -->
      <div class="flex items-center gap-2">
        <button
          @click="handleSendWhatsappSaleInvoice()"
          class="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-green-500 rounded-lg hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-400"
        >
          <i class="fab fa-whatsapp text-base" />
          <span>Send</span>
          <i v-if="isWhatsappLoader" class="fas fa-spinner fa-spin" />
        </button>
        <button
          @click="handlePrint()"
          class="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-gray-700 rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500"
        >
          <i class="fas fa-print" />
          <span>Print</span>
        </button>
      </div>
    </div>
    <div v-if="receipt && receipt.userData">
      <div class="p-3 bg-gray-100">
        <div
          ref="printArea"
          class="max-w-4xl p-4 mx-auto bg-white rounded-lg shadow-md"
        >
          <div class="flex items-center justify-between pb-4 mb-4 border-b">
            <div>
              <h1 class="text-2xl font-bold text-gray-700">Quote</h1>
              <p class="text-sm text-gray-500">
                {{
                  transformInvoiceNumber(
                    receipt.branchName,
                    receipt.date,
                    receipt.invoiceNumber
                  )
                }}
              </p>
              <p class="text-sm text-gray-500">
                Date: {{ receipt.date | dateFormat }}
              </p>
            </div>
            <div class="text-right">
              <p class="text-lg font-bold text-gray-800">{{ branchDisplayName }}</p>
              <p v-if="branchAddressLines[0]" class="text-sm text-gray-500">{{ branchAddressLines[0] }}</p>
              <p v-if="branchAddressLines[1]" class="text-sm text-gray-500">{{ branchAddressLines[1] }}</p>
              <p v-if="branchPhone" class="text-sm text-gray-500">Phone: {{ branchPhone }}</p>
              <p v-if="branchGstin" class="text-sm text-gray-500">GSTIN: {{ branchGstin }}</p>
            </div>
          </div>

          <!-- Recipient Details -->
          <div class="flex items-start justify-between pb-4 mb-4 border-b">
            <div>
              <h3 class="mb-2 text-base font-semibold text-gray-700">
                Bill To:
              </h3>
              <p class="text-sm text-gray-500 capitalize">
                {{
                  receipt.userData.firstName + " " + receipt.userData.lastName
                }}
              </p>
              <p class="text-sm text-gray-500">
                Phone: {{ receipt.userData.phone }}
              </p>
            </div>
            <div class="flex items-start gap-4">
              <div class="text-right">
                <h2 class="text-base font-semibold text-gray-700">Bank Details</h2>
                <p v-if="branchBankName" class="text-sm text-gray-500">Bank : {{ branchBankName }}</p>
                <p v-if="branchAccountHolder" class="text-sm text-gray-500">A/c Holder : {{ branchAccountHolder }}</p>
                <p v-if="branchAccountNumber" class="text-sm text-gray-500">A/c No. : {{ branchAccountNumber }}</p>
                <p v-if="branchIfscCode" class="text-sm text-gray-500">
                  IFSC<template v-if="branchBankBranch"> ({{ branchBankBranch }})</template> : {{ branchIfscCode }}
                </p>
                <p v-if="branchUpiId" class="text-sm text-gray-500">UPI : {{ branchUpiId }}</p>
                <p v-if="branchPhone" class="text-sm text-gray-500">Phone: {{ branchPhone }}</p>
                <p v-if="branchGstin" class="text-sm text-gray-500">GSTIN: {{ branchGstin }}</p>
              </div>
              <div v-if="upiQrDataUrl" class="flex flex-col items-center">
                <img :src="upiQrDataUrl" width="100" height="100" alt="UPI QR" />
                <p class="text-xs text-gray-400 mt-1">Scan to Pay</p>
              </div>
            </div>
          </div>

          <!-- Items Table -->
          <div class="mb-6">
            <table
              class="w-full text-sm border border-collapse border-gray-200"
            >
              <thead>
                <tr class="bg-gray-100">
                  <th class="px-1 py-2 text-left border border-gray-200">
                    Description
                  </th>
                  <th class="px-1 py-2 text-right border border-gray-200">
                    HSN
                  </th>
                  <th class="px-1 py-2 text-right border border-gray-200">
                    GST
                  </th>
                  <th class="px-1 py-2 text-right border border-gray-200">
                    Qty.
                  </th>
                  <th class="px-1 py-2 text-right border border-gray-200">
                    Rate
                  </th>
                  <th class="px-1 py-2 text-right border border-gray-200">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in receipt.productList" :key="index">
                  <td class="px-1 py-2 border border-gray-200">
                    {{ item.name }}
                  </td>
                  <td class="px-1 py-2 border border-gray-200">
                    {{ item.hsn }}
                  </td>
                  <td class="px-1 py-2 border border-gray-200">
                    {{ item.gstRate }}
                  </td>
                  <td class="px-1 py-2 border border-gray-200">
                    {{ item.quantity }}
                  </td>
                  <td class="px-1 py-2 border border-gray-200">
                    {{ item.rate | currency }}
                  </td>
                  <td class="px-1 py-2 text-right border border-gray-200">
                    {{ item.amount | currency }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Footer -->
          <div class="flex items-start justify-between pt-4 border-t">
            <div>
              <h3 class="text-lg font-semibold text-gray-700">Notes</h3>
              <p class="text-sm text-gray-500">
                This is computer generated invoice.
              </p>
            </div>
            <div class="text-right">
              <p class="text-sm text-gray-500">
                CGST:
                <span class="font-semibold">{{ receipt.cgst | currency }}</span>
              </p>
              <p class="text-sm text-gray-500">
                SGST:
                <span class="font-semibold">{{ receipt.sgst | currency }}</span>
              </p>
              <p class="text-sm text-gray-500">
                Round Off:
                <span class="font-semibold">{{
                  receipt.roundOff | currency
                }}</span>
              </p>
              <p class="text-sm text-gray-500">
                Grand Total:
                <span class="font-semibold">
                  {{ receipt.grandTotal | currency }}
                </span>
              </p>
              <p class="text-sm text-gray-500">
                Amount In words:
                <span class="font-semibold">
                  {{ receipt.amountInWords }}
                </span>
              </p>
            </div>
          </div>
          <div class="flex justify-center pt-4 mt-2 border-t">
            <img
              src="../../assets/images/rajput.png"
              width="260"
              height="40"
              alt=""
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import dateFns from "date-fns";
import { apiHelper } from "../../utils/helpers";
import endpoints from "@/utils/constants";
import { printElementAsA4 } from "@/utils/printA4";
import branchDetailsMixin from "@/mixins/branchDetailsMixin";
import QRCode from "qrcode";

export default {
  mixins: [branchDetailsMixin],
  filters: {
    capitalize(value) {
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
    dateFormat(value) {
      return dateFns.format(new Date(value), "Do MMM YYYY");
    },
    currency(value) {
      const amount = parseFloat(value) || 0;
      const a = new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR"
      }).format(amount);
      return a.replace(".00", "");
    }
  },
  data: () => {
    return {
      isWhatsappLoader: false,
      userMobiles: [],
      selectedMobile: null,
      upiQrDataUrl: null
    };
  },
  computed: {
    ...mapGetters({ receipt: "sale/getReceiptData" })
  },
  watch: {
    receipt(val) {
      if (val && val.branchName) {
        this.loadBranchDetails(val.branchName);
      }
    },
    branchUpiId(val) {
      if (val) this.generateUpiQr();
      else this.upiQrDataUrl = null;
    }
  },
  mounted() {
    this.getUserMobiles();
    if (this.receipt && this.receipt.branchName) {
      this.loadBranchDetails(this.receipt.branchName);
    }
  },
  methods: {
    fetchData() {
      this.getUserMobiles();
    },
    resetProperty() {
      this.userMobiles = null; // Reset the property
      this.selectedMobile = null;
    },
    handlePrint() {
      printElementAsA4(this.$refs.printArea, { title: "Sale Invoice" });
    },
    async generateUpiQr() {
      if (!this.branchUpiId || !this.receipt) {
        this.upiQrDataUrl = null;
        return;
      }
      const amount = this.receipt.grandTotal || "";
      const name = encodeURIComponent(this.branchDisplayName);
      const upiString = `upi://pay?pa=${this.branchUpiId}&pn=${name}&am=${amount}&cu=INR`;
      try {
        this.upiQrDataUrl = await QRCode.toDataURL(upiString, { width: 200, margin: 1 });
      } catch {
        this.upiQrDataUrl = null;
      }
    },
    getUserMobiles() {
      const userId = this.receipt && this.receipt.userData && this.receipt.userData.userId;
      if (!userId) {
        this.userMobiles = [];
        return;
      }
      apiHelper
        .call(
          "get",
          `${endpoints.LARAVEL_BASE_URL}/api/v1/users/${userId}/mobiles`
        )
        .then(response => {
          this.userMobiles = response.data.mobiles;
        })
        .catch(() => {
          this.userMobiles = [];
        });
    },
    transformInvoiceNumber(branch, date, invoiceNumber) {
      if (!branch || !date || !invoiceNumber) return '';
      const branchInitials = branch
        .trim()
        .slice(0, 2)
        .toUpperCase();
      const financialYear = this.getFinancialYear(date);
      const paddedNumber = invoiceNumber.toString().padStart(4, "0");
      return branchInitials + financialYear + paddedNumber;
    },
    getFinancialYear(date) {
      const invoiceDate = new Date(date);
      const year = invoiceDate.getFullYear();
      const month = invoiceDate.getMonth() + 1; // Months are 0-indexed in JS

      // If the month is January, February, or March, it's part of the previous financial year
      const startYear = month < 4 ? year - 1 : year;
      const endYear = startYear + 1;

      // Return the financial year in the format 2425
      return `${startYear.toString().slice(-2)}${endYear.toString().slice(-2)}`;
    },
    handleSendWhatsappSaleInvoice() {
      this.isWhatsappLoader = true;
      apiHelper
        .call(
          "post",
          `${endpoints.LARAVEL_BASE_URL}/api/v1/send/sale-invoice/${this.receipt.saleId}`,
          { phone: this.selectedMobile }
        )
        .then(response => {
          this.isWhatsappLoader = false;
          this.userMobiles = [];
          this.selectedMobile = null;
          this.$notify({
            message: "Invoice sent to whtasapp",
            type: "success",
            title: "Success"
          });
        })
        .catch(err => {
          this.$notify({
            message: "Something went wrong",
            type: "danger",
            title: "Error"
          });
        });
    }
  }
};
</script>
